import { useEffect, useMemo, useState } from "react"
import Modal from "Components/molecule/modal"
import SearchSelect from "Components/molecule/inputs/search-select"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import Chip from "Components/atoms/chip"
import { LambdaDependencyOptions } from "models/lambda-tracker"

interface LambdaDependencyEditorProps {
    isOpen: boolean
    onClose: () => void
    options: LambdaDependencyOptions | null
    /** Screen being edited, or null to add a new one. */
    screen: { code: string, name: string } | null
    /** Lambdas currently set for the screen, in run order. */
    initialLambdaIds: string[]
    /** Screens that already have dependencies (not offered when adding). */
    configuredScreenCodes: string[]
    onSave: (screenCode: string, lambdaIds: string[]) => Promise<boolean>
}

// Picks the Lambdas a screen's data depends on and their run order (sources first, reports last).
const LambdaDependencyEditor = ({ isOpen, onClose, options, screen, initialLambdaIds, configuredScreenCodes, onSave }: LambdaDependencyEditorProps) => {
    const [screenCode, setScreenCode] = useState<string>("")
    const [lambdaIds, setLambdaIds] = useState<string[]>([])
    const [isSaving, setIsSaving] = useState<boolean>(false)

    useEffect(() => {
        if (isOpen) {
            setScreenCode(screen?.code ?? "")
            setLambdaIds(initialLambdaIds)
        }
    }, [isOpen])

    const lambdasById = useMemo(() => new Map((options?.lambdas ?? []).map(l => [l.id, l])), [options])

    const screenOptions = useMemo(() => (options?.screens ?? [])
        .filter(s => !configuredScreenCodes.includes(s.code))
        .map(s => ({ value: s.code, label: `${s.name} (${s.url})` })), [options, configuredScreenCodes])

    const lambdaOptions = useMemo(() => (options?.lambdas ?? [])
        .filter(l => !lambdaIds.includes(l.id))
        .map(l => ({ value: l.id, label: `${l.externalSystem} · ${l.displayName}${l.isActive ? "" : " (inactive)"}` })), [options, lambdaIds])

    const move = (index: number, delta: number) => setLambdaIds(prev => {
        const next = [...prev]
        const target = index + delta
        if (target < 0 || target >= next.length) return prev
        ;[next[index], next[target]] = [next[target], next[index]]
        return next
    })

    const save = async () => {
        setIsSaving(true)
        const ok = await onSave(screenCode, lambdaIds)
        setIsSaving(false)
        if (ok) onClose()
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={screen ? `Dependencies of ${screen.name}` : "Add screen dependencies"}
            subtitle="Lambdas whose data this screen shows, in run order: source syncs first, reports last.">
            <div className="lambda-dependency-editor d-flex flex-column gap-3">
                {!screen && (
                    <SearchSelect label="Screen" placeholder="Choose a screen" options={screenOptions} value={screenCode}
                        onChange={o => setScreenCode(o?.value ?? "")} menuPortal isLoading={!options} />
                )}

                <SearchSelect label="Add a Lambda" placeholder="Search Lambdas" options={lambdaOptions} value=""
                    onChange={o => o && setLambdaIds(prev => [...prev, o.value])} menuPortal isLoading={!options} />

                {lambdaIds.length === 0
                    ? <div className="text-muted text-center py-3">No Lambdas. Saving removes every dependency of this screen.</div>
                    : (
                        <ol className="dependency-editor-list">
                            {lambdaIds.map((id, index) => {
                                const lambda = lambdasById.get(id)
                                return (
                                    <li key={id}>
                                        <span className="text-muted num">{index + 1}</span>
                                        <span className="flex-grow-1">
                                            {lambda?.displayName ?? id}
                                            <small className="d-block text-muted">{lambda?.externalSystem}{lambda && !lambda.isActive && <> · <Chip variant="secondary">Inactive</Chip></>}</small>
                                        </span>
                                        <button type="button" className="icon-button" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move ${lambda?.displayName ?? id} up`}>
                                            <i className="mdi mdi-arrow-up" aria-hidden />
                                        </button>
                                        <button type="button" className="icon-button" onClick={() => move(index, 1)} disabled={index === lambdaIds.length - 1} aria-label={`Move ${lambda?.displayName ?? id} down`}>
                                            <i className="mdi mdi-arrow-down" aria-hidden />
                                        </button>
                                        <button type="button" className="icon-button" onClick={() => setLambdaIds(prev => prev.filter(v => v !== id))} aria-label={`Remove ${lambda?.displayName ?? id}`}>
                                            <i className="mdi mdi-close" aria-hidden />
                                        </button>
                                    </li>
                                )
                            })}
                        </ol>
                    )}

                <small className="text-muted">
                    Changes here are kept when the dependencies are regenerated from the code: Lambdas you add stay, and ones you remove are not added back.
                </small>

                <div className="d-flex justify-content-end gap-2">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={save} disabled={isSaving || !screenCode || !options}>
                        {isSaving ? "Saving…" : "Save"}
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    )
}

export default LambdaDependencyEditor
